import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ScoreBadge from "@/components/ScoreBadge";
import ConfidenceBadge from "@/components/ConfidenceBadge";
import LifecycleBadge from "@/components/LifecycleBadge";
import OpportunityBadge from "@/components/OpportunityBadge";
import SelectMenu from "@/components/SelectMenu";
import { fmtPct, fmtNum, timeAgo } from "@/lib/format";

export default function Signals() {
  const [type, setType] = useState("");
  const [minScore, setMinScore] = useState("");
  const [status, setStatus] = useState("");
  const [action, setAction] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [marketScope, setMarketScope] = useState("current");

  // v2.5+ / 2026-07-14: hide resolved / market_closed / stale by default.
  // Server-side default is exclude-archived; pass include_archived=1 only
  // when the user explicitly opts in and no explicit lifecycle filter is set.
  const effectiveStatus = status || undefined;

  const { data, isLoading } = useQuery({
    queryKey: ["signals", { type, minScore, status, action, includeArchived, marketScope }],
    queryFn: async () => (await api.get("/signals", { params: {
      signal_type: type || undefined,
      min_score: minScore || undefined,
      lifecycle_status: effectiveStatus,
      actionability: action || undefined,
      include_archived: !status && includeArchived ? 1 : undefined,
      market_scope: marketScope,
      page: 1, page_size: 50,
    }})).data,
    keepPreviousData: true,
  });

  return (
    <div className="space-y-4" data-testid="signals-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Signals feed</div>
        <h1 className="font-heading text-2xl font-bold text-zinc-100">Signals</h1>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SelectMenu
          testid="filter-signal-type"
          value={type}
          onChange={setType}
          className="w-full sm:w-56"
          options={[
            { value: "", label: "All types" },
            { value: "MULTI_TRADER_CONVERGENCE", label: "Multi-trader convergence" },
            { value: "HIGH_QUALITY_TRADER_ENTRY", label: "High-quality entry" },
            { value: "UNUSUAL_CONVICTION_INCREASE", label: "Unusual conviction" },
            { value: "RAPID_CONSENSUS_CHANGE", label: "Rapid consensus change" },
          ]}
        />
        <SelectMenu
          testid="filter-signal-status"
          value={status}
          onChange={setStatus}
          className="w-52"
          options={[
            { value: "", label: "All statuses" },
            { value: "new", label: "New" },
            { value: "active", label: "Active" },
            { value: "weakening", label: "Weakening" },
            { value: "stale", label: "Stale" },
            { value: "market_closed", label: "Market closed" },
            { value: "resolved_successful", label: "Resolved · Correct" },
            { value: "resolved_unsuccessful", label: "Resolved · Incorrect" },
          ]}
        />

        <SelectMenu
          testid="filter-market-scope"
          value={marketScope}
          onChange={setMarketScope}
          className="w-full sm:w-56"
          options={[
            { value: "current", label: "Current live markets" },
            { value: "historical", label: "Historical / closed markets" },
            { value: "all", label: "All market scopes" },
          ]}
        />
        <SelectMenu
          testid="filter-actionability"
          value={action}
          onChange={setAction}
          className="w-48"
          options={[
            { value: "", label: "Any actionability" },
            { value: "potentially_actionable", label: "Potentially actionable" },
            { value: "limited_remaining_edge", label: "Limited edge" },
            { value: "price_moved", label: "Price moved" },
            { value: "stale", label: "Stale" },
          ]}
        />
        <input
          type="number"
          value={minScore}
          onChange={(e) => setMinScore(e.target.value)}
          placeholder="Min score"
          data-testid="filter-min-score"
          className="h-9 w-24 bg-zinc-900 border border-zinc-800 rounded-md px-2 text-sm text-zinc-200"
        />
        <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer select-none"
               data-testid="toggle-include-archived-label">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            data-testid="toggle-include-archived"
            className="accent-zinc-100"
          />
          Include archived lifecycles
        </label>
      </div>

      <div className="grid md:grid-cols-2 gap-4 stagger-in">
        {isLoading && <div className="text-xs text-zinc-500">Loading signals…</div>}
        {!isLoading && (data?.items || []).length === 0 && (
          <div className="text-xs text-zinc-500 border border-zinc-800 rounded-md p-6 col-span-2 text-center">
            No signals match your filters. Signals are created when tracked traders converge on a market outcome.
          </div>
        )}
        {(data?.items || []).map((s) => (
          <Link
            key={s.id}
            to={`/app/signals/${s.id}`}
            data-testid={`signal-card-${s.id}`}
            className="border border-zinc-800 rounded-md p-4 hover:border-zinc-700 transition-colors block"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  {(s.signal_types && s.signal_types.length > 1
                    ? s.signal_types.map((t) => t.replaceAll("_", " ")).join(" + ")
                    : (s.signal_type || "signal").replaceAll("_", " "))} · {s.market_category || "Uncategorized"}
                </div>
                <div className="mt-1 font-heading font-semibold text-zinc-100 line-clamp-2">
                  {s.market_title || s.market_id}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <OpportunityBadge rating={s.opportunity_rating} size="lg" />
                <div className="flex items-center gap-1">
                  <ConfidenceBadge confidence={s.confidence}
                                    testid={`signal-confidence-${s.id}`} />
                  <ScoreBadge score={s.overall_score} />
                </div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Direction</div>
                <div className="text-zinc-200 mt-0.5">{s.outcome}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Current</div>
                <div className="text-zinc-200 mt-0.5 font-mono-num">{fmtPct(s.current_probability)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Avg entry</div>
                <div className="text-zinc-200 mt-0.5 font-mono-num">{fmtPct(s.estimated_average_entry)}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-[11px] text-zinc-500">
              <span>{s.actionability_label}</span>
              <span>{fmtNum(s.participating_trader_ids?.length)} trader(s) · {timeAgo(s.detected_at)}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
